import last from 'lodash/last';
import { getLastPageUrl } from '../util';
import { INTERCOM, GORGIAS, DIRECT_SMS_CHAT, ZENDESK } from 'constants/conversationTypes';
import { DEFAULT_CONVERSATION_LANGUAGE_CODE } from 'constants/llmTranslation';
import { INBOUND, OUTBOUND, INTERNAL } from 'constants/messageDirection';
import { INTERNAL_NOTE } from 'constants/messageTypes';
import { ESCALATION_REASON, SPECIALIST_ONLY_HINT } from 'constants/noteTypes';
import { SMB } from 'constants/sources';
import AuthService from 'services/authService';

// TODO: Move other message selectors from other files here

export const isMessageSmbReply = (message) => message.source === SMB && message.direction === OUTBOUND;
export const isMessageOutboundFromSimplr = (message) => {
  // any outbound message that is not from the business should be from us
  return message.direction === OUTBOUND && !isMessageSmbReply(message);
};

export const isMessageFromCurrentUser = (message) => {
  const currentUser = AuthService.current();

  return currentUser && message.expert.simplrExpertId === currentUser.simplrExpert.id;
};

export const showAsNote = (message) => {
  return message.direction === INTERNAL || message.messageType === INTERNAL_NOTE;
};

export const shouldHideInternalNote = (noteType, isReviewTicket = false, isAdmin) => {
  if (isReviewTicket) {
    return noteType === SPECIALIST_ONLY_HINT;
  }

  if (!isAdmin) {
    return noteType === ESCALATION_REASON || noteType === SPECIALIST_ONLY_HINT;
  }

  return false;
};

export const getMessagesInConversation = (selectedTicket, conversation, user, isReviewTicket) => {
  let messages = [];
  const lastPageUrl = getLastPageUrl({ ticket: selectedTicket });
  let isFirstInboundMessage = false;

  conversation.allMessages.forEach((messageId, index) => {
    const message = Object.assign({}, conversation[messageId]);

    if (
      message.messageType === INTERNAL_NOTE &&
      shouldHideInternalNote(message.noteType, isReviewTicket, user.isAdmin)
    ) {
      return;
    }

    if (!isFirstInboundMessage && message.direction === INBOUND) {
      isFirstInboundMessage = true;
      message.isFirstInboundMessage = true;
      message.lastPageUrl = lastPageUrl;
    } else {
      message.isFirstInboundMessage = false;
    }
    messages.push(message);
  });

  return messages;
};

export const hasNewMessage = (previousConversation, currentConversation) => {
  const previousLastMessage = last(previousConversation);
  const currentLastMessage = last(currentConversation);

  return (
    (previousLastMessage && previousLastMessage.messageId) !== (currentLastMessage && currentLastMessage.messageId)
  );
};

export const isAttachmentAllowed = (message) => {
  const allowedAttachmentsPlatforms = [INTERCOM, GORGIAS, DIRECT_SMS_CHAT, ZENDESK];

  return allowedAttachmentsPlatforms.includes(message?.messageType);
};

/**
 * Returns the message’s content, language, and translation status based on the passed params.
 *
 * @param {object} message - message model object.
 * @param {string} ticketLanguage - ticket’s detected language (conversation.language).
 * @param {bool} [isTicketTranslated=false] - whether the conversation has been translated or not.
 * @param {bool} [displayForeignLanguage=false] - whether the user has selected to display messages in foreign language or not.
 * @return {Object}
 * @property {string} content - message content to render.
 * @property {string} language - language of the content to render.
 * @property {string|null} translatedFrom - indicates if the content has been translated from another language.
 *
 * @example getMessageDataToRender(message, 'en', false, false)
 *  > { language: 'en', content: 'messageBody', translatedFrom: null }
 * @example getMessageDataToRender(message, 'fr', true, true)
 *  > { language: 'fr', content: 'translatedMessage', translatedFrom: 'en' }
 */
export const getMessageDataToRender = (
  message,
  ticketLanguage,
  isTicketTranslated = false,
  displayForeignLanguage = false
) => {
  let language = DEFAULT_CONVERSATION_LANGUAGE_CODE;
  let content = !!message.messageBodyHtml ? message.messageBodyHtml : message.messageBody;
  let translatedFrom = null;

  if (!isTicketTranslated) {
    return { language, content, translatedFrom };
  }

  if ([INBOUND, OUTBOUND].includes(message.direction)) {
    if (message.direction === INBOUND) {
      if (!displayForeignLanguage && message.translatedMessage) {
        content = message.translatedMessage;
        translatedFrom = ticketLanguage;
      }
    } else if (message.direction === OUTBOUND) {
      content = message?.originalMessage ?? message.messageBody;

      if (displayForeignLanguage && message.translatedMessage) {
        content = message.translatedMessage;
        translatedFrom = DEFAULT_CONVERSATION_LANGUAGE_CODE;
      }
    }

    if (displayForeignLanguage && message.translatedMessage) {
      language = ticketLanguage;
    }
  }

  return { language, content, translatedFrom };
};

/*
 * Ensures that if the message has been translated, the English content is returned.
 *
 * @param {object} Message
 * @return {string} result - messageBody or translatedMessage.
 */
export const getEnglishMessageBody = (message) => {
  let result = message.messageBody;

  if (message.direction === INBOUND && message.translatedMessage) {
    result = message.translatedMessage;
  } else if (message.direction === OUTBOUND && message.translatedMessage) {
    result = message.originalMessage;
  }

  return result;
};
